import { useState, useCallback, useEffect } from 'react';
import { useAdminFetch } from '../api/fetch';
import { useNotify } from '../components/NotificationProvider';

export interface Video {
  id: string;
  fileName: string;
  videoName: string;
  order: number;
  watchedStudents: number;
  answeredStudents: number;
  startTime?: string;
  endTime?: string;
  testResult?: string;
  testTime?: string;
  name?: string; // CSVエクスポート用
  watchedSeconds?: number; // CSVエクスポート用：視聴合計時間（秒）
}

export interface Lecture {
  id: string;
  lectureName: string;
  videos: Video[];
  studentsNumber: number;
  attendedStudents: number;
}

export interface UnattendedLectureData {
  id: string;
  email: string;
  videos?: string;
}

export interface UnattendedVideoData {
  id: string;
  email: string;
  videoStatus: boolean;
}

export interface UseLectureDataProps {
  onError?: (message: string) => void;
}

export interface AlertState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

export const useLectureData = ({ onError }: UseLectureDataProps = {}) => {
  const notify = useNotify();
  const handleError = onError ?? notify;
  const [lectureData, setLectureData] = useState<Lecture[]>([]);
  const [unattendedLectureData, setUnattendedLectureData] = useState<UnattendedLectureData[]>([]);
  const [unattendedVideoData, setUnattendedVideoData] = useState<UnattendedVideoData[]>([]);
  const [videoOrderSuccess, setVideoOrderSuccess] = useState(false);
  const [videoOrderFail, setVideoOrderFail] = useState(false);
  const [alertState, setAlertState] = useState<AlertState>({ open: false, message: "", severity: "success" });
  const { get, post } = useAdminFetch();

  const refresh = useCallback(async () => {
    try {
      const res = await get<{
        message: string;
        lectures: Lecture[];
      }>('/api/admin/lectures/videos');
      
      console.log(res.message);
      setLectureData(res.lectures);
    } catch (error) {
      handleError("Can't get data from db properly.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getVideo = useCallback(async (fileName: string) => {
    try {
      const res = await get<{ url: string }>(`/api/admin/video/${fileName}`);
      console.log(res.url);
      return res.url;
    } catch (error) {
      handleError("Can't get Video from db properly.");
      return null;
    }
  }, [get, handleError]);

  const getUnattendedLectureData = useCallback(async (id: string) => {
    try {
      const res = await get<{
        message: string;
        unattendedLectureData: UnattendedLectureData[];
      }>(`/api/admin/lecture/unattended/${id}`);
      
      console.log(res.message);
      setUnattendedLectureData(res.unattendedLectureData);
    } catch (error) {
      handleError("Can't get data from db properly.");
    }
  }, [get, handleError]);

  const getUnattendedVideoData = useCallback(async (id: string) => {
    try {
      const res = await get<{
        message: string;
        unattendedVideoData: UnattendedVideoData[];
      }>(`/api/admin/video/unattended/${id}`);
      
      console.log(res.message);
      setUnattendedVideoData(res.unattendedVideoData);
    } catch (error) {
      handleError("Can't get data from db properly.");
    }
  }, [get, handleError]);

  // アラート表示のためのユーティリティ関数
  const showAlert = useCallback((message: string, severity: "success" | "error") => {
    setAlertState({
      open: true,
      message,
      severity
    });
  }, []);

  // アラートを閉じる
  const closeAlert = useCallback(() => {
    setAlertState(prev => ({ ...prev, open: false }));
  }, []);

  // videoOrderの成功/失敗を監視
  useEffect(() => {
    if (videoOrderSuccess) {
      showAlert("動画の順番が正常に変更されました。", "success");
    } else if (videoOrderFail) {
      showAlert("動画の順番を変更できませんでした。", "error");
    }
  }, [videoOrderSuccess, videoOrderFail, showAlert]);

  const updateVideoOrder = useCallback(async (lectureId: string, videos: Video[]) => {
    const newLectureData = [...lectureData];
    const targetLecture = newLectureData.find((lecture) => lecture.id === lectureId);
    
    if (targetLecture) {
      targetLecture.videos = videos;
      const videoOrder = videos.map((video) => video.id);
      
      try {
        const res = await post<{
          message: string;
          success: boolean;
        }>('/api/admin/video/update', {
          body: JSON.stringify({
            lectureId,
            videoOrder: JSON.stringify(videoOrder),
          }),
        });
        
        console.log(res.message);
        if (res.success) {
          setLectureData(newLectureData);
          setVideoOrderSuccess(true);
          showAlert("動画の順番が正常に変更されました。", "success");
          setTimeout(() => setVideoOrderSuccess(false), 3000);
          return true;
        } else {
          setVideoOrderFail(true);
          showAlert("動画の順番を変更できませんでした。", "error");
          setTimeout(() => setVideoOrderFail(false), 3000);
          return false;
        }
      } catch (error) {
        console.error('Error:', error);
        setVideoOrderFail(true);
        showAlert("動画の順番を変更できませんでした。", "error");
        setTimeout(() => setVideoOrderFail(false), 3000);
        return false;
      }
    }
    return false;
  }, [lectureData, post, showAlert]);

  return {
    lectureData,
    unattendedLectureData,
    unattendedVideoData,
    videoOrderSuccess,
    videoOrderFail,
    refresh,
    getVideo,
    getUnattendedLectureData,
    getUnattendedVideoData,
    updateVideoOrder,
    alertState,
    closeAlert
  };
};